'use client';

import { useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";

import Image from 'next/image';
import { motion } from "framer-motion";
import { FiZoomIn } from "react-icons/fi";

type ZoomableImage = {
  src: string;
  alt: string;
};

type Props = {
  images: ZoomableImage[];
};

export default function ZoomableImageGrid({ images }: Props) {
  const [index, setIndex] = useState(-1);

  return (
    <div className="my-12">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {images.map((img, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="group relative cursor-pointer"
            onClick={() => setIndex(i)}
          >
            {/* Image Container */}
            <div className="relative aspect-[16/10] overflow-hidden rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 shadow-sm transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-indigo-500/10 group-hover:-translate-y-1">
              <Image
                src={img.src}
                alt={img.alt}
                width={1024} // or desired width
                height={576} // or desired height
                className="object-contain w-full h-auto transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />
                          
              {/* Minimalist Hover Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500 flex items-center justify-center">
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ opacity: 1, scale: 1 }}
                  className="p-4 bg-white/20 backdrop-blur-xl rounded-full border border-white/30 text-white opacity-0 group-hover:opacity-100 transition-all"
                >
                  <FiZoomIn size={20} />
                </motion.div>
              </div>
            </div>

            {/* Subtle Caption (Outside the box) */}
            {img.alt && (
              <p className="mt-3 px-2 text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {img.alt}
              </p>
            )}
          </motion.div>
        ))}
      </div>

      <Lightbox
        open={index >= 0}
        index={index}
        close={() => setIndex(-1)}
        slides={images.map(img => ({ 
          src: img.src, 
          title: img.alt,
          description: "Technical Preview"
        }))}
        plugins={[Zoom, Captions]}
        animation={{ fade: 400 }}
        controller={{ closeOnBackdropClick: true }}
        styles={{
          container: { backgroundColor: "rgba(0, 0, 0, 0.95)" },
        }}
      />
    </div>
  );
}